import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Instagram as Pinterest, Instagram as TikTok, Mail, MessageCircle } from 'lucide-react';

export default function Footer() {
  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/HubluSA', Icon: Facebook },
    { name: 'Instagram', url: 'https://www.instagram.com/hublu.store/', Icon: Instagram },
    //{ name: 'YouTube', url: 'https://www.youtube.com/@hublustore', Icon: Youtube },
    { name: 'Pinterest', url: 'https://za.pinterest.com/Hubluonline/', Icon: Pinterest },
    { name: 'TikTok', url: 'https://www.tiktok.com/@hubluonline.co.za', Icon: TikTok }
  ];

  return (
    <footer className="bg-[hsl(30,35%,32%)] text-[hsl(var(--cream-white))] pt-20 pb-10 mt-auto border-t-[8px] border-[hsl(var(--primary))]">
      {/*
        ACCESSIBILITY: background darkened from natural-oak (HSL 30,40%,45%)
        to HSL(30,35%,32%). The original only gave pure white text a 4.16:1
        contrast ratio against it -- below the 4.5:1 AA threshold for normal
        text, and the /60 and /80 opacity variants used throughout this
        footer fared worse (3.96:1 and 3.08:1). This darker shade gives
        white ~7.8:1 and /80-opacity cream-white ~5.6:1, both comfortably
        passing AA.
      */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img 
                src="https://hubluonline.co.za/images/04208033ef846f3bbe651a2d41a99d5c.png" 
                alt="HUBLU Logo" 
                className="w-28 md:w-32 h-auto object-contain"
              />
            </Link>
            <p className="text-[hsl(var(--cream-white))]/80 leading-relaxed max-w-sm text-sm">
              Crafting joyous moments for families across South Africa with premium wooden toys, Montessori essentials, and durable pet products.
            </p>
          </div>

          {/* Navigation Links */}
          <div>
            <h3 className="font-semibold text-lg mb-6 text-white tracking-wide">Explore</h3>
            <ul className="space-y-3">
              {[
                { name: 'Shop', path: '/shop' },
                { name: 'About', path: '/about' },
                { name: 'Blog', path: '/blog' },
                { name: 'Contact', path: '/contact' },
                { name: 'FAQ', path: '/faq' }
              ].map(item => (
                <li key={item.name}>
                  <Link to={item.path} className="text-sm text-[hsl(var(--cream-white))]/80 hover:text-[hsl(var(--warm-sand))] transition-colors duration-200 inline-flex items-center group">
                    <span className="w-0 h-0.5 bg-[hsl(var(--warm-sand))] mr-0 group-hover:w-3 group-hover:mr-2 transition-all duration-300"></span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policy Links */}
          <div>
            <h3 className="font-semibold text-lg mb-6 text-white tracking-wide">Policies</h3>
            <ul className="space-y-3">
              {[
                { name: 'Terms of Service', path: '/terms' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Shipping Policy', path: '/shipping' },
                { name: 'Returns & Refunds', path: '/returns' },
                { name: 'POPIA Compliance', path: '/popia' }
              ].map(item => (
                <li key={item.name}>
                  <Link to={item.path} className="text-sm text-[hsl(var(--cream-white))]/80 hover:text-[hsl(var(--warm-sand))] transition-colors duration-200 inline-flex items-center group">
                    <span className="w-0 h-0.5 bg-[hsl(var(--warm-sand))] mr-0 group-hover:w-3 group-hover:mr-2 transition-all duration-300"></span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-6 text-white tracking-wide">Get in Touch</h3>
            <ul className="space-y-4 mb-6">
              <li>
                <a href="mailto:info@hubluonline.co.za" className="flex items-center gap-3 text-sm text-[hsl(var(--cream-white))]/80 hover:text-white transition-colors duration-200 group">
                  <span className="p-2 bg-white/10 rounded-full group-hover:bg-[hsl(var(--primary))] transition-colors"><Mail className="w-4 h-4" /></span>
                  info@hubluonline.co.za
                </a>
              </li>
              <li>
                <a href="https://wa.me/27723330922" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-[hsl(var(--cream-white))]/80 hover:text-white transition-colors duration-200 group">
                  <span className="p-2 bg-white/10 rounded-full group-hover:bg-[hsl(160,70%,50%)] transition-colors"><MessageCircle className="w-4 h-4" /></span>
                  WhatsApp Us
                </a>
              </li>
            </ul>
            <div className="p-4 bg-black/10 rounded-xl border border-white/5">
              <p className="text-xs text-[hsl(var(--cream-white))]/80 mb-1">Business Registration</p>
              <p className="text-sm font-medium text-white tracking-wider">2025/846025/07</p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex gap-3">
            {socialLinks.map(({ name, url, Icon }) => (
              <a 
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-[hsl(var(--primary))] hover:-translate-y-1 transition-all duration-300"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
          <div className="text-center md:text-right text-sm text-[hsl(var(--cream-white))]/80">
            <p className="mb-1">&copy; 2026 HUBLU. All rights reserved.</p>
            <p>Designed and hosted by <a href="https://apexforge.co.za" target="_blank" rel="noopener noreferrer" className="text-white underline underline-offset-2 hover:text-[hsl(var(--warm-sand))] transition-colors">The Apex Forge</a></p>
          </div>
        </div>
      </div>
    </footer>
  );
}